import axios from 'axios';
import _ from 'lodash';
import { setSegmentModelMetadata } from '@foxcorp/delta-web-analytics';
import {
  axiosErrorHandler,
  helperGetApiHeaders,
  helperConvertPathToApiRoute,
  helperConvertApiRoute,
} from 'helpers/ApiHelper';
import { PAGE_COLLECTION } from 'helpers/PageHelper';
import {
  setVideoPanel as setGlobalVideoPanel,
  clearVideo as clearGlobalVideo,
} from 'components/VideoPlayerGlobal/Actions';
import { concatToUrl, isStaticRender, LOCAL_CHANNEL_STATION_PICKER } from 'helpers/ContentHelper';
import { getCookie, setCookie } from 'helpers/Storage';
import { replaceScreenUrl as optimizelyReplaceCollections } from 'helpers/OptimizelyHelper';
import { autoplayEnabled } from 'helpers/ConfigHelper';
import { getDmaFromVideo } from 'helpers/AuthHelper';
import { fetchFooter } from 'components/Footer/FooterActions';
import { getCurrentShowcase, setShowcaseVideoDma } from './Helpers';

export const CLEAR_COLLECTION_PANELS = 'CLEAR_COLLECTION_PANELS';
export const FETCH_COLLECTION = 'FETCH_COLLECTION';
const FETCH_COLLECTION_PANEL = 'FETCH_COLLECTION_PANEL';
const FETCH_NEXT_CLIPS_PAGE = 'FETCH_NEXT_CLIPS_PAGE';
const SET_NEXT_CLIPS_PAGE = 'SET_NEXT_CLIPS_PAGE';
export const SET_COLLECTION = 'SET_COLLECTION';
export const SET_COLLECTION_USE_SHOWCASE_FALLBACK = 'SET_COLLECTION_USE_SHOWCASE_FALLBACK';
export const SET_COLLECTION_FORCE_KEYART_FOR_SHOWCASE = 'SET_COLLECTION_FORCE_KEYART_FOR_SHOWCASE';
export const UPDATE_VISIBLE_ELEMENTS = 'UPDATE_VISIBLE_ELEMENTS';
export const UPDATE_COLLECTION_ORDER = 'UPDATE_COLLECTION_ORDER';
export const UPDATE_SHOWCASE_VIDEO = 'UPDATE_SHOWCASE_VIDEO';
export const INCREMENT_PANEL_RENDER_COUNT = 'INCREMENT_PANEL_RENDER_COUNT';
export const RESET_PANEL_RENDER_COUNT = 'RESET_PANEL_RENDER_COUNT';

function fetchCollection(path, accessToken, screenName, deviceId, coordinates) {
  const pageType = 'home';
  const replaceCollectionScreen = optimizelyReplaceCollections(pageType, screenName);
  const collectionPath = replaceCollectionScreen || path;
  const request = axios
    .get(
      concatToUrl(helperConvertPathToApiRoute(collectionPath, coordinates)),
      helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId }),
    )
    .catch(axiosErrorHandler);

  return {
    type: FETCH_COLLECTION,
    payload: request,
  };
}

function fetchLocalCollection(path, accessToken, screenName, deviceId, coordinates) {
  return dispatch =>
    dispatch(fetchCollection(path, accessToken, screenName, deviceId, coordinates)).then(result => {
      const localPanels = result.payload?.data?.panels;
      // The first response provides us a list of local categories, we then select the first
      // call sign here in LocalData, the first in this list is what the backend determines is
      // the users call sign based on their location
      const localData = localPanels?.member[0]?.items?.member[0] ?? null;
      const localStationPickerId = getCookie({ name: LOCAL_CHANNEL_STATION_PICKER });
      let localId = localData ? localData.id : 'local-fallback';
      if (localStationPickerId) {
        localId = localStationPickerId;
      } else {
        setCookie({
          name: LOCAL_CHANNEL_STATION_PICKER,
          options: { path: '/' },
          value: localId,
        });
      }
      const localPath = `categories/${localId}`;
      return dispatch(fetchCollection(localPath, accessToken, screenName, deviceId, coordinates));
    });
}

export function setNextClipsPage(url) {
  return {
    type: SET_NEXT_CLIPS_PAGE,
    payload: url,
  };
}

export function fetchNextClipsPage({ url, accessToken, deviceId }) {
  let request = {};
  if (url) {
    const headers = helperGetApiHeaders({ pathKey: 'local', accessToken, deviceId });
    request = axios.get(helperConvertApiRoute(url), headers).catch(axiosErrorHandler);
  }
  return {
    type: FETCH_NEXT_CLIPS_PAGE,
    payload: request,
  };
}

function setCollection(collection, initialShowcase) {
  return {
    type: SET_COLLECTION,
    payload: {
      collection,
      initialShowcase,
    },
  };
}

export function clearCollectionPanels() {
  return { type: CLEAR_COLLECTION_PANELS };
}

export function setUseShowcaseFallback(payload) {
  return { type: SET_COLLECTION_USE_SHOWCASE_FALLBACK, payload };
}

function fetchCollectionPanel(panel, accessToken, deviceId) {
  const { '@id': url } = panel;
  const request = axios
    .get(url, helperGetApiHeaders({ accessToken, deviceId }))
    .catch(axiosErrorHandler);
  return {
    type: FETCH_COLLECTION_PANEL,
    payload: request,
  };
}

export function fetchCollections(
  path,
  panelsToPrefetch,
  accessToken,
  screenName,
  deviceId,
  coordinates,
) {
  return (dispatch, getState) => {
    let collection;
    let fetch = fetchCollection;
    if (screenName?.toLowerCase()?.includes('local')) {
      fetch = fetchLocalCollection;
    }
    return dispatch(fetch(path, accessToken, screenName, deviceId, coordinates)).then(result => {
      const experimentData = _.get(result, 'payload.data.trackingData.properties.abtest', null);
      const experimentKey = _.get(experimentData, 'experimentKey', null);
      const variationKey = _.get(experimentData, 'variationKey', null);
      const dma = getDmaFromVideo(_.get(result, 'payload.data'));

      setSegmentModelMetadata(result?.payload?.data?.trackingData?.properties?.modelMetaData ?? {});

      collection = _.get(result, 'payload.data.panels', {});
      collection.seo = _.get(result, 'payload.data.SEO', null);
      collection.categoryType = result?.payload?.data?.categoryType;
      if (experimentKey && variationKey)
        collection.experimentData = `${experimentKey}:${variationKey}`;
      const screenPanels = _.get(collection, 'member');
      if (!screenPanels) {
        return Promise.resolve();
      }
      return Promise.all(
        screenPanels.map((screenPanel, index) => {
          if (!screenPanel) return Promise.resolve();
          const { items, member, types } = screenPanel;
          const panelIsNotPopulated = !items && !member && !types;
          const prefetchPanel = panelIsNotPopulated && index < panelsToPrefetch;
          if (prefetchPanel) {
            return dispatch(fetchCollectionPanel(screenPanel, accessToken, deviceId)).then(item => {
              const data = _.get(item, 'payload.data');
              _.set(collection, `member.${index}`, data);
              return Promise.resolve();
            });
          }
          return Promise.resolve();
        }),
      )
        .then(() => {
          const showcase = getCurrentShowcase(collection.member);
          if (!isStaticRender()) {
            const newVideoId = _.get(showcase, 'items.member.0.id');
            const oldVideoId = _.get(getState(), 'globalVideo.panelVideo.id');
            const videoChanged = newVideoId && newVideoId !== oldVideoId;
            const shouldSetGlobalVideo = autoplayEnabled() && videoChanged;
            if (shouldSetGlobalVideo) {
              const panel = setShowcaseVideoDma({ showcase, dma });
              const source = PAGE_COLLECTION;
              dispatch(setGlobalVideoPanel({ panel, source }));
            }
          }
          return dispatch(setCollection(collection, showcase));
        })
        .then(() => dispatch(fetchFooter()));
    });
  };
}

export function fetchCollectionsOnHeaderClick(
  path,
  panelsToPrefetch,
  accessToken,
  screenName,
  deviceId,
) {
  return dispatch => {
    if (autoplayEnabled()) {
      dispatch(clearGlobalVideo({ source: PAGE_COLLECTION }));
    }

    dispatch(clearCollectionPanels());
    return dispatch(fetchCollections(path, panelsToPrefetch, accessToken, screenName, deviceId));
  };
}

export function updateVisibleElements(currentElements) {
  return {
    type: UPDATE_VISIBLE_ELEMENTS,
    payload: currentElements,
  };
}

export function updateCollectionOrder(panel) {
  const id = _.get(panel, 'id', null);
  return {
    type: UPDATE_COLLECTION_ORDER,
    payload: id,
  };
}

export function forceKeyartForShowcase(value) {
  return {
    type: SET_COLLECTION_FORCE_KEYART_FOR_SHOWCASE,
    payload: value,
  };
}

export function updateShowcaseVideo(video) {
  return {
    type: UPDATE_SHOWCASE_VIDEO,
    payload: video,
  };
}

export function incrementPanelRenderCount() {
  return {
    type: INCREMENT_PANEL_RENDER_COUNT,
  };
}

export function resetPanelRenderCount() {
  return {
    type: RESET_PANEL_RENDER_COUNT,
  };
}
